import React from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import BaseComponent from 'components/BaseComponent';
import styledComp from 'components/StyledComp';
import DataTables from 'components-desktop/DataTables';
import TfootSummary from 'components-desktop/TfootSummary';
import helpers from 'misc/helpers';

const propTypes = {
  title: PropTypes.string,
  claims: PropTypes.array,
};

const defaultProps = {
  title: '',
  claims: [],
};

@autobind
class TableEraClaims extends BaseComponent {
  constructor(props) {
    super(props);
  }

  rowClassName(record, index) {
    return !record.invoiceMatched ? 'danger' : undefined;
  }

  renderStatus(text, record) {
    const statusName = { 1: 'Normal', 4: 'Denied', 22: 'Reversal' }[text] || text;
    const statusColor = { 1: 'green_light', 4: 'red', 22: 'yellow_gold' }[text] || 'textPrimaryDark';

    return (
      <styledComp.Span
        width="wrap_content"
        height="wrap_content"
        color={statusColor}
        fontSize="12px"
      >
        {statusName}
      </styledComp.Span>
    );
  }

  createColumns() {
    return [{
      title: 'Invoice #',
      dataIndex: 'invoiceNumber',
      sorter: (a, b) => a.invoiceNumber?.localeCompare(b.invoiceNumber),
      defaultSortOrder: 'ascend',
      render: (text, record) => (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          href={`/invoices/invoice_details?id=${record.invoiceId}`}
          target="_blank"
        >
          {text}
        </styledComp.A>
      ),
    }, {
      title: 'Claim #',
      dataIndex: 'claimNumber',
      sorter: (a, b) => a.claimNumber?.localeCompare(b.claimNumber),
    }, {
      title: 'Status',
      dataIndex: 'status',
      sorter: (a, b) => a.status?.localeCompare(b.status),
      render: this.renderStatus,
    }, {
      title: 'Patient',
      dataIndex: 'patientName',
      sorter: (a, b) => a.patientName?.localeCompare(b.patientName),
      render: (text, record) => (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          href={`/patients/patient_profile?id=${record.patientId}`}
          target="_blank"
        >
          {text}
        </styledComp.A>
      ),
    }, {
      title: 'Auth Code',
      dataIndex: 'authorizationCode',
      sorter: (a, b) => a.authorizationCode?.localeCompare(b.authorizationCode),
    }, {
      title: 'Inv Date',
      dataIndex: 'invoiceDateUsString',
      sorter: (a, b) => a.invoiceDate - b.invoiceDate,
    }, {
      title: 'Inv Amount',
      dataIndex: 'invoiceAmountInDollars',
      sorter: (a, b) => a.invoiceAmount - b.invoiceAmount,
    }, {
      title: 'Clm Billed',
      dataIndex: 'billedInDollars',
      sorter: (a, b) => a.billed - b.billed,
    }, {
      title: 'Clm Paid',
      dataIndex: 'paidInDollars',
      sorter: (a, b) => a.paid - b.paid,
      render: (text, record) => (
        <styledComp.Span
          width="wrap_content"
          height="wrap_content"
          color="textPrimaryDark"
          fontSize="12px"
          fontWeight="bold"
        >
          {text}
        </styledComp.Span>
      ),
    }, {
      title: 'Balance Due After',
      dataIndex: 'balanceDueAfterInDollars',
      sorter: (a, b) => a.balanceDueAfter - b.balanceDueAfter,
    }, {
      title: 'Note',
      dataIndex: 'note',
      sorter: (a, b) => a.note?.localeCompare(b.note),
      render: (text, record) => (
        <styledComp.Span
          width="wrap_content"
          height="wrap_content"
          color="red"
          fontSize="12px"
        >
          {text}
        </styledComp.Span>
      ),
    }];
  }

  createDataSource(claims) {
    const dataSource = [];
    claims.forEach((claim, index) => {
      const invoice = claim.getRemoteObject('invoice');
      dataSource.push({
        key: `${claim.getString('invoiceNumber')}_${claim.getString('claimNumber')}`,
        invoiceId: invoice?.getId(),
        invoiceNumber: claim.getString('invoiceNumber'),
        claimNumber: claim.getString('claimNumber'),
        status: claim.getString('status'),
        patientId: invoice?.getLong('patientId'),
        patientName: invoice?.getString('patientName'),
        authorizationCode: invoice?.getString('authorizationCode'),
        invoiceDate: invoice?.getLong('invoiceDate'),
        invoiceDateUsString: invoice?.getString('invoiceDateUsString'),
        invoiceAmount: invoice?.getDouble('amount'),
        invoiceAmountInDollars: helpers.toDollars(invoice?.getDouble('amount')),
        billed: claim.getDouble('billed'),
        billedInDollars: helpers.toDollars(claim.getDouble('billed')),
        paid: claim.getDouble('paid'),
        paidInDollars: helpers.toDollars(claim.getDouble('paid')),
        balanceDueAfter: (invoice?.getDouble('balanceDue') || 0) - claim.getDouble('paid'),
        balanceDueAfterInDollars: helpers.toDollars(
          invoice && ((invoice?.getDouble('balanceDue') || 0) - claim.getDouble('paid'))),
        invoiceMatched: claim.getBoolean('invoiceMatched'),
        note: !claim.getBoolean('invoiceMatched') ? 'No Invoice Matched' : '',
      });
    });
    return dataSource;
  }

  renderTfoot(columnsToDisplay, dataRowsToDisplay) {
    return (
      <TfootSummary
        columnsToDisplay={columnsToDisplay}
        dataRowsToDisplay={dataRowsToDisplay}
        isTableSelectable={false}
        columnKeysToAlign={['billedInDollars', 'paidInDollars']}
        reducers={[
          TfootSummary.SUM_REDUCER_GENERATOR('billed'),
          TfootSummary.SUM_REDUCER_GENERATOR('paid'),
        ]}
        formatters={[
          n => helpers.toDollars(n),
          n => helpers.toDollars(n),
        ]}
      />
    );
  }

  renderContent() {
    const { claims, title, ...layoutProps } = this.props;

    return (
      <DataTables
        {...layoutProps}
        ref={(ref) => { this.dataTables = ref; }}
        color="yellow_gold"
        fontSize="12px"
        size="small"
        title={title}
        icon="money-collect"
        withColumnFilter={false}
        rowClassName={this.rowClassName}
        columns={this.createColumns()}
        dataSource={this.createDataSource(claims)}
        tfoot={this.renderTfoot}
      />
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
TableEraClaims.propTypes = propTypes;
TableEraClaims.defaultProps = defaultProps;

export default TableEraClaims;
